import { Table as ComponentsTable } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import React from "react";
import { FileDrop } from "react-file-drop";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Column } from "react-table";

import workflowStyle from "./all-workflows-table.scss";
import { createAllWorkflowsTableColumns } from "./AllWorkflowsTableColumns";
import ConnectedWorkflowsList from "./ConnectedWorkflowsList";
import { determineEditorGenerationByProfileAndVersion } from "./manage-workflow-dialog/ManageWorkflowDialog";
import ProductNameColumn from "./ProductNameColumn";
import SubWorkflowList from "./SubWorkflowList";
import WorkflowEventDatePicker from "./WorkflowEventDatePicker";
import {
    compareEditorVersionDtos,
    deduceLatestEditorVersions,
    determineEditorToOpen,
    isNewerEditorAvailable,
    mergeAndSortEditorVersions,
    trackFetchLoaded,
} from "./WorkflowUtil";
import Button from "components/button/Button";
import FeatureUpdateModal from "components/feature-update/FeatureUpdateModal";
import Chevron from "components/icons/Chevron";
import KebabMenu from "components/kebab-menu/KebabMenu";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import SearchView from "components/search/SearchView";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import DateCell from "components/table/DateCell";
import Table, { deriveColumnWidth } from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import Tooltip from "components/tooltip/Tooltip";
import { ActiveWorkflowUserList } from "components/workflows/ActiveWorkflowUserList";
import ImportWorkflowsDialog from "components/workflows/ImportWorkflowsDialog";
import ManageWorkflowWrapper from "components/workflows/manage-workflow-dialog/ManageWorkflowWrapper";
import SidePanel from "components/workflows/SidePanel";
import WorkflowEditorV3View, {
    InitialTab,
    VersionRestoreParams,
} from "components/workflows/workflow-editor-v3/WorkflowEditorV3View";
import WorkflowKebabMenu from "components/workflows/WorkflowKebabMenu";
import {
    AUTH_USER_VIEW,
    AUTH_WORKFLOW_ENABLE,
    AUTH_WORKFLOW_LAUNCH,
    AUTH_WORKFLOW_SCHEDULE,
    AUTH_WORKFLOW_SECURED,
} from "domain/authority";
import { FeatureLicenseType } from "domain/users";
import {
    ConnectedWorkflows,
    FilterState,
    LaunchType,
    ScheduleStatus,
    WorkflowEntityType,
    WorkflowTableData,
} from "domain/workflows";
import {
    featureFlagService,
    FLAG_UICOMPS_WORKFLOWS_TABLE,
    FLAG_WORKFLOW_EDITOR_V3,
} from "services/feature/FeatureFlagService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie, isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { userService } from "services/user/users/UserService";
import { userSessionService } from "services/user/UserSessionService";
import {
    clearWorkflowRestoreVersionDetails,
    getWorkflowRestoreVersionDetails,
} from "services/workflows/workflowRestoreVersionRepository";
import {
    EditorGeneration,
    isProfile,
    ManifestWorkflowEditor,
    Profile,
    profileToFeatureLicenseMap,
    profileToNameMap,
    QuickFilter,
    WorkflowEditorV3SupportedEntry,
    workflowService,
    WorkflowService,
} from "services/workflows/WorkflowService";
import { store, StoreState } from "store";
import { setUser } from "store/user";
import buttons from "styles/buttons.scss";
import { default as form, default as formStyle } from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { logger } from "utils/logging";
import { RepositoryKey } from "utils/repository";

import assetGradingIcon from "assets/images/icons/assetGradingIcon.svg";
import lockIcon from "assets/images/icons/darkGreyLockIcon.svg";
import outlinedInfo from "assets/images/icons/outlinedInfo.svg";
import illustration from "assets/images/update-workflows-dialog/illustration.svg";

import testIds from "testIds.json";

interface TableState {
    workflowsData: WorkflowTableData[];
    count: number;
    cursor: string;
    scrollPosition: number | undefined;
}

const mapState = (state: StoreState) => ({
    user: state.userReducer.user,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
    workflow: state.workflowReducer.workflowRestoreVersion,
});
const connector = connect(mapState, { setUser });

const AllWorkflowsTable = (
    props: ConnectedProps<typeof connector> & {
        count: number;
        search: string;
        filterState?: FilterState;
        setFilterState?: React.Dispatch<React.SetStateAction<FilterState>>;
        onUpdate: () => void;
        workflowService: WorkflowService;
    }
): JSX.Element => {
    const { t } = useTranslation();
    const [tableState, setTableState] = React.useState<TableState>({
        workflowsData: [],
        count: 0,
        cursor: "",
        scrollPosition: undefined,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [loading, setLoading] = React.useState<boolean>(false);
    const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
    const [workflowUuid, setWorkflowUuid] = React.useState<string>();
    const [workflowProduct, setWorkflowProduct] = React.useState("");
    const [workflowVersion, setWorkflowVersion] = React.useState("");
    const [editorGeneration, setEditorGeneration] = React.useState<EditorGeneration | undefined>(undefined);
    const [manageDialogVisible, setManageDialogVisible] = React.useState(false);
    const [importDialogVisible, setImportDialogVisible] = React.useState(false);
    const [importWorkflowFileList, setImportWorkflowFileList] = React.useState<FileList | undefined>(undefined);
    const [search, setSearchQuery] = React.useState("");
    const [workflowEditors, setWorkflowEditors] = React.useState<ManifestWorkflowEditor[]>([]);
    const [workflowEditorsLoaded, setWorkflowEditorsLoaded] = React.useState(false);
    const [optionList, setOptionList] = React.useState<JSX.Element[] | undefined>(undefined);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [selectedVersionSearch, setSelectedVersionSearch] = React.useState("");
    const [selectedProductSearch, setSelectedProductSearch] = React.useState("");
    const [selectedRow, setSelectedRow] = React.useState<WorkflowTableData | undefined>(undefined);
    const [userUuid, setUserUuid] = React.useState("");
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const workflowEditorV3Flag = Boolean(useFeature(FLAG_WORKFLOW_EDITOR_V3));
    const [workflowEditorV3Supported, setWorkflowEditorV3Supported] = React.useState<WorkflowEditorV3SupportedEntry[]>(
        []
    );
    const [workflowEditorV3SupportedLoaded, setWorkflowEditorV3SupportedLoaded] = React.useState(false);
    const [showEditorV3, setShowEditorV3] = React.useState(false);
    const [editorV3Tabs, setEditorV3Tabs] = React.useState<InitialTab[] | undefined>(undefined);
    const [editWorkflowVisible, setEditWorkflowVisible] = React.useState(false);
    const [pendingOpenV3, setPendingOpenV3] = React.useState(false);
    const [selectedConnectedData, setSelectedConnectedData] = React.useState<ConnectedWorkflows | undefined>(undefined);
    const [modalTitle, setModalTitle] = React.useState<string>(t("ImportWorkflowDialog.title"));
    const [modalTitleIcon, setModalTitleIcon] = React.useState<JSX.Element | undefined>(undefined);
    const [isSidePanelOpen, setIsSidePanelOpen] = React.useState<boolean>(false);

    const filters = [QuickFilter.ALL, QuickFilter.CREATOR];
    const [activeButton, setActiveButton] = React.useState<QuickFilter>(QuickFilter.ALL);

    const filterTestIds = new Map<QuickFilter, string>([
        [QuickFilter.ALL, testIds.workArea.workflows.filter.allButton],
        [QuickFilter.CREATOR, testIds.workArea.workflows.filter.creatorButton],
    ]);

    const filterTranslationKeys = new Map<QuickFilter, string>([
        [QuickFilter.ALL, "WorkflowsTable.filter.all"],
        [QuickFilter.CREATOR, "WorkflowsTable.filter.CreatedByYou"],
    ]);

    const [restoreVersionId, setRestoreVersionId] = React.useState<string | undefined>(undefined);

    // TODO BCC-8002: Remove isWorkflowsTableFeatureEnabled and related checks after Workflows table rewrite is complete.
    const isWorkflowsTableFeatureEnabled = featureFlagService.isFeatureEnabled(FLAG_UICOMPS_WORKFLOWS_TABLE);

    const openWithV3 = (initialTabs: InitialTab[]) => {
        setEditorV3Tabs(initialTabs);
        setShowEditorV3(true);
        setManageDialogVisible(false);
        setEditWorkflowVisible(false);
    };

    const showManageDialog = (
        uuid: string,
        version: string,
        product: string,
        connectedWorkflows: ConnectedWorkflows
    ) => {
        const decision = determineEditorToOpen(
            product,
            version,
            workflowEditorV3Supported,
            workflowEditors,
            workflowEditorV3Flag,
            determineEditorGenerationByProfileAndVersion
        );

        setSelectedConnectedData(connectedWorkflows);
        setWorkflowUuid(uuid);
        setEditorGeneration(decision.editorGeneration);
        setWorkflowVersion(version);
        setWorkflowProduct(product);
        setRestoreVersionId(restoreVersionId);

        if (connectedWorkflows.parentWorkflows.length > 0) {
            setPendingOpenV3(decision.openV3);
            setEditWorkflowVisible(true);
            return;
        }

        if (decision.openV3) {
            openWithV3([{ workflowUuid: uuid }]);
        } else {
            setManageDialogVisible(true);
        }
    };

    const openManageDialogAfterConfirmation = (visible: boolean) => {
        if (visible && pendingOpenV3) {
            if (workflowUuid === undefined) {
                throw new Error("Workflow UUID must be set before confirming to open the V3 editor.");
            }
            openWithV3([{ workflowUuid: workflowUuid }]);
        } else {
            setManageDialogVisible(visible);
        }
    };

    const hideImportDialog = () => {
        setImportDialogVisible(false);
        setImportWorkflowFileList(undefined);
    };
    const showImportDialog = (fileList: FileList) => {
        setImportWorkflowFileList(fileList);
        setImportDialogVisible(true);
    };

    const handleStartDateChange = (date: string) => {
        handleFilterChanges({ from: date });
    };

    const handleEndDateChange = (date: string) => {
        handleFilterChanges({ to: date });
    };

    const handleFilterChanges = (update: Partial<FilterState>) => {
        if (props.setFilterState) {
            props.setFilterState((prevState) => ({
                ...prevState,
                ...update,
            }));
        }
    };

    const versionValue = selectedVersionSearch.includes("Erasure")
        ? selectedVersionSearch.replace("Erasure", "")
        : selectedVersionSearch.replace("Diagnostics", "");

    const fetchData = (initialLoading: boolean, abortController: AbortController, userUuid?: string) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        props.workflowService
            .fetchWorkflows(
                search,
                initialLoading ? "" : tableState.cursor,
                selectedProductSearch.toUpperCase(),
                versionValue.trimEnd(),
                props.filterState?.from,
                props.filterState?.to,
                userUuid,
                abortController
            )
            .then((data) => {
                if (!abortController.signal.aborted) {
                    setTableState((prevState) => ({
                        ...prevState,
                        scrollPosition: initialLoading ? undefined : prevState.workflowsData.length - 1,
                        workflowsData: initialLoading
                            ? data.workflowTableData
                            : prevState.workflowsData.concat(data.workflowTableData),
                        count: data.count,
                        cursor: data.cursor,
                    }));
                    setLoading(false);
                    setRequestFailureMessage("");
                }
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("WorkflowsTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                }
            });
    };

    const handleFilterClick = (filter: QuickFilter) => {
        abortControllers.forEach((controller) => controller.abort());
        setActiveButton(filter);
        setIsSidePanelOpen(false);
        setSelectedRow(undefined);
        setTableState({ workflowsData: [], count: 0, cursor: "", scrollPosition: undefined });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        if (filter === QuickFilter.ALL) {
            setUserUuid("");
            fetchData(true, abortController);
        } else if (filter === QuickFilter.CREATOR && props.user?.uuid) {
            setUserUuid(props.user.uuid);
            fetchData(true, abortController, props.user.uuid);
        }
    };

    const handleFileDrop = (files: FileList) => {
        if (files.length === 0) {
            return;
        }
        usageStatisticsService.sendEvent({
            category: Category.WORKFLOW,
            action: Action.IMPORT_WORKFLOW,
        });
        showImportDialog(files);
    };

    const updateShowWorkflowUpdateDialogValue = () => {
        if (props.user) {
            const updatedUser = { ...props.user, workflowUpdates: {}, showWorkflowUpdates: false };
            props.setUser(updatedUser);
            userSessionService.storeUser(updatedUser);
        }
    };

    const hideFeatureUpdateModal = () => {
        usageStatisticsService.sendEvent({
            category: Category.WORKFLOW,
            action: Action.CLOSE_WORKFLOW_UPDATES_DIALOG,
        });
        if (!props.user?.uuid) {
            return;
        }
        updateShowWorkflowUpdateDialogValue();
        userService.deleteWorkflowUpdates(props.user.uuid).catch(() => {
            logger.error("Failed to reset user's workflow updates.");
        });
    };

    const createDropdownArrow = (isExpanded: boolean) => {
        const theme = store.getState();
        return (
            <div>
                <Chevron
                    color={theme.themeReducer.theme.iconFillColor}
                    className={classNames(workflowStyle.toggleArrow, workflowStyle.down, {
                        [workflowStyle.up]: isExpanded,
                    })}
                    width={20}
                    height={20}
                />
            </div>
        );
    };

    const closeSidePanel = () => {
        setIsSidePanelOpen(false);
    };

    // TODO BCC-8002: Remove const columns after Workflows table rewrite is completed
    const columns: Array<Column<WorkflowTableData>> = [];
    columns.push(
        {
            Header: () => <TextWithTooltip text={t("WorkflowsTable.name")} key="name" />,
            accessor: "name",
            Cell: (cellInfo) => {
                const { profile, secured, uuid, editorVersion, connectedWorkflows } = cellInfo.cell.row.original;

                const isBmpSecured = profile === Profile.BMP && secured;
                const hasSecuredAuth = userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SECURED]);
                const isRestrictedSecured = isBmpSecured && (!isCurrentUserInternal() || !hasSecuredAuth);

                const canLaunchWorkflow =
                    userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_LAUNCH]) &&
                    cellInfo.row.original.launchType === LaunchType.MANUAL &&
                    profile === Profile.BMP;

                const canScheduleWorkflow =
                    userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SCHEDULE]) &&
                    cellInfo.row.original.launchType === LaunchType.MANUAL &&
                    profile === Profile.BMP;

                const canEnableWorkflow =
                    userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_ENABLE]) &&
                    cellInfo.row.original.launchType !== LaunchType.MANUAL &&
                    profile === Profile.BMP &&
                    !hasTenantCookie();

                return (
                    <>
                        {(!isRestrictedSecured || canLaunchWorkflow || canScheduleWorkflow || canEnableWorkflow) && (
                            <KebabMenu preventEventPropagation={true}>
                                <WorkflowKebabMenu
                                    version={cellInfo.cell.row.original.editorVersion}
                                    uuid={cellInfo.cell.row.original.uuid}
                                    product={cellInfo.cell.row.original.profile}
                                    name={cellInfo.cell.row.original.name}
                                    enabled={cellInfo.cell.row.original.enabled}
                                    secured={cellInfo.cell.row.original.secured}
                                    own={cellInfo.cell.row.original.own}
                                    defaultWorkflow={cellInfo.cell.row.original.defaultWorkflow.own}
                                    onUpdate={props.onUpdate}
                                    workflowService={workflowService}
                                    editorGeneration={cellInfo.row.original.editorGeneration}
                                    connectedWorkflows={cellInfo.row.original.connectedWorkflows}
                                    onOpenWithV3={openWithV3}
                                    updateVisible={isNewerEditorAvailable(
                                        cellInfo.row.original.profile,
                                        cellInfo.row.original.editorGeneration,
                                        cellInfo.row.original.editorVersion,
                                        latestEditorVersions,
                                        workflowEditorV3Supported
                                    )}
                                    setIsSidePanelOpen={setIsSidePanelOpen}
                                    launchType={cellInfo.row.original.launchType}
                                    parameters={cellInfo.row.original.parameters}
                                    setSelectedRow={setSelectedRow}
                                    rowData={cellInfo.cell.row.original}
                                />
                            </KebabMenu>
                        )}
                        {isRestrictedSecured ? (
                            <div className={workflowStyle.nameContainer}>
                                {isBmpSecured && (
                                    <Tooltip content={t("WorkflowsTable.setWorkflowSecured.tooltip")}>
                                        <img
                                            src={lockIcon}
                                            alt="secured"
                                            style={{ marginRight: 4, width: 14, height: 14 }}
                                        />
                                    </Tooltip>
                                )}
                                <TextWithTooltip text={cellInfo.value} />
                            </div>
                        ) : (
                            <button
                                className={workflowStyle.nameLinkContainer}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showManageDialog(uuid, editorVersion, profile, connectedWorkflows);
                                    setIsSidePanelOpen(false);
                                }}
                            >
                                {isBmpSecured && (
                                    <Tooltip content={t("Common.secured")}>
                                        <img
                                            src={lockIcon}
                                            alt="secured"
                                            style={{ marginRight: 4, width: 14, height: 14 }}
                                        />
                                    </Tooltip>
                                )}
                                {cellInfo.cell.row.original.assetGrading && (
                                    <Tooltip content={t("workflowTemplate.assetGrading")}>
                                        <img
                                            src={assetGradingIcon}
                                            alt="asset grading"
                                            style={{ marginRight: 4, width: 14, height: 14 }}
                                        />
                                    </Tooltip>
                                )}

                                <TextWithTooltip text={cellInfo.value} />
                            </button>
                        )}
                    </>
                );
            },
            width: deriveColumnWidth(30, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("WorkflowsTable.product")} key="product" />,
            accessor: "product",
            Cell: ({ cell: { row } }) => (
                <ProductNameColumn
                    profile={row.original.product.toLowerCase()}
                    version={row.original.editorVersion}
                    generation={row.original.editorGeneration}
                    t={t}
                />
            ),
            width: deriveColumnWidth(30, tableContainerRef),
        },
        {
            Header: () => (
                <TextWithTooltip text={t("WorkflowsTable.statusTooltip")}>
                    <div className={workflowStyle.statusTooltip}>
                        <div>{t("Common.status")}</div>
                        <div>
                            <img src={outlinedInfo} alt="status info" />
                        </div>
                    </div>
                </TextWithTooltip>
            ),
            accessor: "defaultWorkflow",
            id: "defaultWorkflow.other.usernames",
            Cell: ({ cell: { value, row } }) => {
                const own = value.own;
                const usernames = value.other.userDetails;
                const buttonRef = React.useRef(null);
                return row.original.launchType != LaunchType.MANUAL ? (
                    <>
                        <TextWithTooltip text={""}>
                            {row.original.profile === Profile.BMP ? (
                                row.original.enabled ? (
                                    <div className={workflowStyle.activeBadge}>{t("Common.active")}</div>
                                ) : (
                                    <div className={workflowStyle.inactiveBadge}>{t("Common.inactive")}</div>
                                )
                            ) : (
                                own && <div className={workflowStyle.activeBadge}>{t("WorkflowsTable.default")}</div>
                            )}

                            {row.original.profile !== Profile.BMP && usernames.length > 0 && (
                                <>
                                    {!own ? (
                                        <div className={workflowStyle.activeWorkflowColumn}>
                                            {t("WorkflowsTable.defaultFor")}
                                        </div>
                                    ) : (
                                        " +"
                                    )}

                                    <div className={workflowStyle.activeWorkflowColumn}>
                                        {t("WorkflowsTable.users", { count: value.other.count })}
                                    </div>
                                    {userSessionService.userHasAllAuthorities([AUTH_USER_VIEW]) && (
                                        <button
                                            ref={buttonRef}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                row.toggleRowExpanded();
                                                usageStatisticsService.sendEvent({
                                                    category: Category.WORKFLOW,
                                                    action: Action.SHOW_USER_LIST_IN_WORKFLOW,
                                                });
                                                setIsSidePanelOpen(false);
                                            }}
                                            data-testid={testIds.workArea.workflows.table.defaultList.button}
                                            aria-expanded={row.isExpanded}
                                        >
                                            {createDropdownArrow(row.isExpanded)}
                                        </button>
                                    )}

                                    {row.isExpanded && (
                                        <ActiveWorkflowUserList
                                            status={buttonRef}
                                            userDetails={row.original.defaultWorkflow.other.userDetails}
                                            own={row.original.defaultWorkflow.own}
                                            userCount={row.original.defaultWorkflow.other.count}
                                        />
                                    )}
                                </>
                            )}
                        </TextWithTooltip>
                    </>
                ) : (
                    <>
                        {row.original.schedule !== undefined && row.original.schedule !== ScheduleStatus.NONE && (
                            <StatusBadge
                                testId={testIds.workArea.workflows.table.scheduleBadge}
                                dataType={
                                    row.original.schedule === ScheduleStatus.ACTIVE
                                        ? ScheduleStatus.ACTIVE
                                        : ScheduleStatus.INACTIVE
                                }
                                values={[
                                    {
                                        status:
                                            row.original.schedule === ScheduleStatus.ACTIVE
                                                ? Status.SUCCESS
                                                : Status.NEUTRAL,
                                        title: t("Common.scheduled"),
                                    },
                                ]}
                            />
                        )}
                    </>
                );
            },
            width: deriveColumnWidth(25, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("WorkflowsTable.createdDate")} key="createdDate" />,
            accessor: "createdDate",
            Cell: ({ cell: { value } }) => <DateCell tooltip={true} value={value} />,
            width: deriveColumnWidth(20, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("WorkflowsTable.creator")} key="creator" />,
            accessor: "creator",
            Cell: ({ cell: { value } }) => <TextWithTooltip text={value} />,
            width: deriveColumnWidth(35, tableContainerRef),
        }
    );
    columns.splice(3, 0, {
        Header: () => (
            <TextWithTooltip
                text={t("WorkflowsTable.connectedWorkflows.title")}
                key="defaultWorkflow.connectedWorkflows"
            />
        ),
        accessor: "connectedWorkflows",
        Cell: ({ cell: { value } }) => {
            return value.totalConnectedWorkflows ? (
                <ConnectedWorkflowsList connectedWorkflows={value} isSubWorkflow={false} />
            ) : (
                <></>
            );
        },
        width: deriveColumnWidth(25, tableContainerRef),
    });

    const latestEditorVersions = workflowEditors.length > 0 ? deduceLatestEditorVersions(workflowEditors) : undefined;

    const tableColumns = createAllWorkflowsTableColumns({
        t,
        tableContainerRef,
        workflowService,
        latestEditorVersions,
        workflowEditorV3Supported,
        onUpdate: props.onUpdate,
        openWithV3,
        showManageDialog,
        setIsSidePanelOpen,
        setSelectedRow,
    });

    React.useEffect(() => {
        const abortController = new AbortController();
        trackFetchLoaded(
            props.workflowService.fetchWorkflowEditors(Profile.ALL, abortController),
            abortController,
            setWorkflowEditors,
            setWorkflowEditorsLoaded,
            "Failed to fetch workflow editor manifest; the Update option will not be shown for outdated workflows."
        );

        const v3AbortController = new AbortController();
        if (workflowEditorV3Flag) {
            trackFetchLoaded(
                props.workflowService.fetchWorkflowEditorV3Supported(v3AbortController),
                v3AbortController,
                setWorkflowEditorV3Supported,
                setWorkflowEditorV3SupportedLoaded,
                "Failed to fetch V3 supported versions; V3 editor options will not be shown."
            );
        }

        return () => {
            abortController.abort();
            v3AbortController.abort();
        };
    }, [workflowEditorV3Flag]);

    React.useEffect(() => {
        setManageDialogVisible(false);
        setEditWorkflowVisible(false);
        const abortController = new AbortController();
        setTableState({ workflowsData: [], count: 0, cursor: "", scrollPosition: undefined });
        fetchData(true, abortController, userUuid);
        return () => {
            abortController.abort();
        };
    }, [
        props.count,
        search,
        selectedProductSearch.toUpperCase(),
        selectedVersionSearch,
        props.filterState?.from,
        props.filterState?.to,
    ]);

    React.useEffect(() => {
        const sortedEditors = workflowEditors
            .filter((editor) =>
                selectedProductSearch === Profile.BDE ||
                selectedProductSearch === Profile.LUN ||
                selectedProductSearch === Profile.BMP
                    ? editor.profile === selectedProductSearch
                    : editor.profile !== Profile.BDE && editor.profile !== Profile.LUN && editor.profile !== Profile.BMP
            )
            .sort(compareEditorVersionDtos);

        const removeDuplicateVersion = sortedEditors.filter(
            (obj, index, self) => index === self.findIndex((each) => each.version === obj.version)
        );

        const allVersions = mergeAndSortEditorVersions(
            removeDuplicateVersion.map((entry) => entry.version),
            workflowEditorV3Supported,
            selectedProductSearch
        );
        setOptionList(allVersions.map((version) => <option key={version}>{version}</option>));
    }, [selectedProductSearch, workflowEditorV3Supported]);

    let dataCount = null;
    if (tableState.workflowsData.length > 0) {
        dataCount = t("Common.recordsCount", { dataCount: tableState.workflowsData.length });
    }

    const handleRestoreVersion = (
        uuid: string,
        editorVersion: string,
        profile: string,
        restoreVersionId: string,
        editorGeneration?: EditorGeneration,
        connectedWorkflows?: ConnectedWorkflows
    ) => {
        setSelectedConnectedData(connectedWorkflows);
        setWorkflowUuid(uuid);
        setEditorGeneration(editorGeneration);
        setWorkflowVersion(editorVersion);
        setWorkflowProduct(profile);
        setRestoreVersionId(restoreVersionId);
        setManageDialogVisible(true);
    };

    const handleVersionRestoreFromV3 = (params: VersionRestoreParams) => {
        const profile = params.product.toLowerCase();
        if (!isProfile(profile)) {
            throw new Error(`Unknown profile received from workflow editor: ${params.product}`);
        }
        setShowEditorV3(false);

        const decision = determineEditorToOpen(
            profile,
            params.editorVersion,
            workflowEditorV3Supported,
            workflowEditors,
            workflowEditorV3Flag,
            determineEditorGenerationByProfileAndVersion
        );

        handleRestoreVersion(
            params.workflowUuid,
            params.editorVersion,
            profile,
            params.versionId,
            decision.editorGeneration
        );
    };

    const tableReady =
        !initialLoading && workflowEditorsLoaded && (!workflowEditorV3Flag || workflowEditorV3SupportedLoaded);

    const table = (
        <div className={classNames(!tableReady ? workflowStyle.loadingIndicator : "", workflowStyle.tableStyle)}>
            {isWorkflowsTableFeatureEnabled ? (
                <div className={layoutStyle.tableInnerWrapper}>
                    <ComponentsTable
                        key={tableReady ? "loaded" : "loading"}
                        tableIdentity={RepositoryKey.WORKFLOWS_TABLE}
                        data={tableState.workflowsData}
                        columns={tableColumns}
                        loaded={tableReady}
                        failureMessage={requestFailureMessage}
                        scrollTo={tableState.scrollPosition}
                        emptyStateMessage={t("WorkflowsTable.emptyStateMessage")}
                        isSidePanelOpen={isSidePanelOpen}
                        onOpenSidePanel={(row) => {
                            setSelectedRow(row);
                            setIsSidePanelOpen(true);
                        }}
                        sidePanel={
                            <SidePanel
                                onClose={closeSidePanel}
                                isSidePanelOpen={isSidePanelOpen}
                                selectedRow={selectedRow}
                                testId={testIds.workArea.workflows.sidePanel.itself}
                                type={WorkflowEntityType.WORKFLOW}
                                onRestoreVersion={handleRestoreVersion}
                                onOpenWithV3={(workflowUuid, versionId) => openWithV3([{ workflowUuid, versionId }])}
                                workflowEditorV3Supported={workflowEditorV3Supported}
                                onRefreshTable={props.onUpdate}
                                onCloseSidePanel={closeSidePanel}
                            />
                        }
                        onCloseSidePanel={closeSidePanel}
                        testIds={{ itself: testIds.workArea.workflows.table.itself }}
                    />
                </div>
            ) : (
                <>
                    <Table
                        key={tableReady ? "loaded" : "loading"}
                        tableIdentity={RepositoryKey.WORKFLOWS_TABLE}
                        data={tableState.workflowsData}
                        columns={columns}
                        loaded={tableReady}
                        loading={loading}
                        failureMessage={requestFailureMessage}
                        tooltips={true}
                        scrollTo={tableState.scrollPosition}
                        emptyMessage={t("WorkflowsTable.emptyStateMessage")}
                        data-testId={testIds.workArea.workflows.table}
                        noAction={true}
                        showSidePanel={true}
                        setIsSidePanelOpen={setIsSidePanelOpen}
                        setSelectedRow={setSelectedRow}
                        isSidePanelOpen={isSidePanelOpen}
                    />
                    <SidePanel
                        onClose={closeSidePanel}
                        isSidePanelOpen={isSidePanelOpen}
                        selectedRow={selectedRow}
                        testId={testIds.workArea.workflows.sidePanel.itself}
                        type={WorkflowEntityType.WORKFLOW}
                        onRestoreVersion={handleRestoreVersion}
                        onOpenWithV3={(workflowUuid, versionId) => openWithV3([{ workflowUuid, versionId }])}
                        workflowEditorV3Supported={workflowEditorV3Supported}
                        onRefreshTable={props.onUpdate}
                        onCloseSidePanel={closeSidePanel}
                    />
                </>
            )}
        </div>
    );
    const tableWrapper = hasTenantCookie() ? table : <FileDrop onDrop={handleFileDrop}>{table}</FileDrop>;

    let bmpWorkflowsProfilesOption: JSX.Element = <></>;

    if (
        props.tenantDetails?.featureLicenses.includes(profileToFeatureLicenseMap.get(Profile.BMP) as FeatureLicenseType)
    ) {
        bmpWorkflowsProfilesOption = (
            <option key={"BMP"} value={Profile.BMP}>
                {profileToNameMap.get(Profile.BMP)}
            </option>
        );
    }

    React.useEffect(() => {
        const details = getWorkflowRestoreVersionDetails();
        if (details) {
            setWorkflowUuid(details.workflowUuid);
            setEditorGeneration(
                determineEditorGenerationByProfileAndVersion(
                    details.profile.toLowerCase() as Profile,
                    details.editorVersion,
                    true
                )
            );
            setWorkflowVersion(details.editorVersion);
            setWorkflowProduct(details.profile);
            setRestoreVersionId(details.versionId);

            setManageDialogVisible(true);
            clearWorkflowRestoreVersionDetails();
        }
    }, []);

    return (
        <>
            <div className={layoutStyle.aboveTable}>
                <div className={classNames(layoutStyle.recordCount, workflowStyle.quickFilterContainer)}>
                    {filters.map((filter) => (
                        <button
                            onClick={() => {
                                handleFilterClick(filter);
                            }}
                            key={filter}
                            className={classNames(workflowStyle.quickFilterButton, {
                                [workflowStyle.active]: activeButton === filter,
                            })}
                            data-testid={filterTestIds.get(filter) || testIds.workArea.workflows.filter.allButton}
                        >
                            {t(filterTranslationKeys.get(filter) || "WorkflowsTable.filter.all")}
                        </button>
                    ))}
                    <div className={workflowStyle.countContainer}>{dataCount}</div>
                </div>
                <div className={classNames(workflowStyle.filter, workflowStyle.filterLongContent)}>
                    <div className={formStyle.search}>
                        <select
                            id={"product"}
                            value={selectedProductSearch}
                            onChange={(event) => {
                                setSelectedProductSearch(event.target.value);
                                setSelectedVersionSearch("");
                            }}
                            className={classNames(formStyle.select, formStyle.fixedWidthInput)}
                            data-testid={testIds.workArea.workflows.filter.productSelect}
                        >
                            <option key={"empty"} value={""}>
                                {t("WorkflowsTable.filter.allProducts")}
                            </option>
                            {Object.values(Profile).map((each, index) => {
                                if (
                                    Profile.ALL !== each &&
                                    Profile.BMP !== each &&
                                    props.tenantDetails?.featureLicenses.includes(
                                        profileToFeatureLicenseMap.get(each) as FeatureLicenseType
                                    )
                                ) {
                                    return (
                                        <option key={index} value={each}>
                                            {profileToNameMap.get(each)}
                                        </option>
                                    );
                                }
                            })}
                            {bmpWorkflowsProfilesOption}
                        </select>
                    </div>
                    <div className={formStyle.search}>
                        <select
                            name="version"
                            value={selectedVersionSearch}
                            onChange={(e) => setSelectedVersionSearch(e.target.value)}
                            className={classNames(form.select, workflowStyle.fixedWidthVersion)}
                            disabled={selectedProductSearch === ""}
                            data-testid={testIds.workArea.workflows.filter.versionSelect}
                        >
                            <option key={"value"} value={""}>
                                {t("WorkflowsTable.filter.allVersions")}
                            </option>
                            {optionList}
                        </select>
                    </div>
                    <div>
                        <WorkflowEventDatePicker from={handleStartDateChange} to={handleEndDateChange} />
                    </div>

                    <div className={formStyle.search}>
                        <SearchView setSearch={setSearchQuery} searchInProgress={false} />
                    </div>
                </div>
            </div>
            <div className={classNames(layoutStyle.tableWrapper, workflowStyle.tableWithSidePanel)}>{tableWrapper}</div>
            {tableState.cursor != null &&
                tableState.workflowsData.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttons.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false, new AbortController(), userUuid);
                            usageStatisticsService.sendEvent({
                                label: "workflows",
                                action: Action.LOAD_MORE,
                                category: Category.WORKFLOW,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
            <WorkflowEditorV3View
                isOpen={showEditorV3}
                onClose={() => setShowEditorV3(false)}
                onVersionRestore={handleVersionRestoreFromV3}
                initialTabs={editorV3Tabs}
                entityType={WorkflowEntityType.WORKFLOW}
            />
            <ManageWorkflowWrapper
                modalTitle={"ManageWorkflowDialog.title.manage"}
                modalVisibility={manageDialogVisible}
                onUpdate={props.onUpdate}
                workflowUuid={workflowUuid}
                editorGeneration={editorGeneration}
                workflowService={props.workflowService}
                onShowModal={setManageDialogVisible}
                onOpenWithV3={openWithV3}
                isEdit={true}
                product={workflowProduct}
                version={workflowVersion}
                parentWorkflowsCount={selectedConnectedData?.parentWorkflows.length}
                entityType={WorkflowEntityType.WORKFLOW}
                restoreVersionId={restoreVersionId}
            />
            <Modal
                isOpen={importDialogVisible}
                hideModal={hideImportDialog}
                modalTitle={modalTitle}
                titleIcon={modalTitleIcon}
            >
                <ImportWorkflowsDialog
                    fileList={importWorkflowFileList != null ? Array.from(importWorkflowFileList) : undefined}
                    onClose={hideImportDialog}
                    service={workflowService}
                    setModalTitle={setModalTitle}
                    setModalTitleIcon={setModalTitleIcon}
                />
            </Modal>
            <SubWorkflowList
                visible={editWorkflowVisible}
                hide={setEditWorkflowVisible}
                openManageDialogVisible={openManageDialogAfterConfirmation}
                connectedWorkflows={selectedConnectedData}
            />
            <FeatureUpdateModal
                open={
                    props.user?.showWorkflowUpdates === true ||
                    Object.keys(props.user?.workflowUpdates ?? {}).length > 0
                }
                hide={hideFeatureUpdateModal}
                showBmpHighlights={props.user?.showWorkflowUpdates === true}
                title={t("Common.whatsNew")}
                banner={<img src={illustration} />}
                actions={
                    <Button
                        onClick={() => {
                            hideFeatureUpdateModal();
                        }}
                        type={"submit"}
                        variant={"PRIMARY"}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
                testId={testIds.workArea.workflows.workflowFeatureUpdateDialog.itself}
                bmpHighlights={{
                    title: "Introducing Blancco Management Portal automations!",
                    message: "The Workflow Editor now supports BMP workflows. ",
                    highlights: [
                        {
                            title: t("FeatureUpdateModal.bmpHighlights.bmpWorkflowtrigger.title"),
                            text: t("FeatureUpdateModal.bmpHighlights.bmpWorkflowtrigger.text"),
                        },
                        {
                            title: t("FeatureUpdateModal.bmpHighlights.workflowSidePanel.title"),
                            text: t("FeatureUpdateModal.bmpHighlights.workflowSidePanel.text"),
                        },
                        {
                            title: t("FeatureUpdateModal.bmpHighlights.bmpWorkflowDailyQuota.title"),
                            text: t("FeatureUpdateModal.bmpHighlights.bmpWorkflowDailyQuota.text"),
                        },
                        {
                            title: t("FeatureUpdateModal.bmpHighlights.bmpWorkflowTestMode.title"),
                            text: t("FeatureUpdateModal.bmpHighlights.bmpWorkflowTestMode.text"),
                        },
                    ],
                    profile: Profile.BMP,
                }}
            />
        </>
    );
};

export default connector(AllWorkflowsTable);
